import IconCheckedSuccess from "../../../../components/atoms/IconCheckedSuccess"
import { TENANTS } from "../../../../constants"
import { loc } from "../../../../core/utils"
import TextField from "../../../../form/TextField"
import { useOfferRentalVoucher } from "../../../../hooks"
import { patchOrder } from "../../../../service/patchOrder"
import { normalizeFinalPageConfiguration } from "../../utils/normalizeFinalPageConfiguration"
import { validateFinalPageFormValues } from "../../validator/finalPageFormValidation"
import ImportFileUploader from "../ImportFileUploader"
import "./styles.css"
import createDecorator from "final-form-focus"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Col, Row, Spinner } from "react-bootstrap"
import { Form as FinalForm } from "react-final-form"
import { useMutation } from "react-query"

const requestDocumentCategory = "DELIVERY_DOCUMENT"

const focusOnErrors = createDecorator()

/** N° commande dist. réel : en base `no_commande_dist` est souvent initialisé à l'OPL — on ne l'affiche pas comme saisie distributeur. */
function initialSupplierOrderNumber(order, opl) {
  const raw = order?.supplierOrderNumber
  if (raw == null || String(raw).trim() === "") return ""
  const oplStr = opl != null ? String(opl).trim() : ""
  if (oplStr !== "" && String(raw).trim() === oplStr) return ""
  return String(raw).trim()
}

export default function PdiRetailAcceptanceContent({ tunnelData }) {
  const {
    tenant,
    apiUrl,
    access_token,
    data: { order }
  } = tunnelData
  const finalPageNorm = useMemo(
    () => normalizeFinalPageConfiguration(tunnelData.finalPageConfiguration),
    [tunnelData.finalPageConfiguration]
  )

  const initialFormValues = useMemo(
    () => ({
      finalPageForm: {
        id_distributeur: order.externalIdPartenaire || "",
        supplierOrderNumber: initialSupplierOrderNumber(order, tunnelData.opl),
        supplierBillNumber: order?.supplierBillNumber ?? "",
        numero_marquage: order?.bikeNumber ?? "",
        imei: order?.imeiNumber ?? "",
        opl: order.opl ?? ""
      }
    }),
    [order, tunnelData.opl]
  )

  const partnerOrderReadOnly =
    finalPageNorm.id_commande_distributeur &&
    (Boolean(order?.supplierBillNumber) ||
      (Boolean(order?.supplierOrderNumber) &&
        String(order.supplierOrderNumber).trim() !== "" &&
        order.supplierOrderNumber !== tunnelData.opl))
  const isMediaMarkt = tenant === TENANTS.MEDIAMARKT_NL || tenant === TENANTS.MEDIAMARKT_BE
  const isConforamaOrBut = tenant === TENANTS.CONFORAMA || tenant === TENANTS.BUT || tenant === TENANTS.CONFORAMA_RETAIL
  const isConforamaRetailSupplierBillNumber = tenant === TENANTS.CONFORAMA_RETAIL && finalPageNorm.supplier_bill_number
  const shouldAutoSubmitFinalPage = isConforamaOrBut && !isConforamaRetailSupplierBillNumber

  const hasAutoSubmittedRef = useRef(false)

  useEffect(() => {
    if (!shouldAutoSubmitFinalPage || hasAutoSubmittedRef.current) return
    hasAutoSubmittedRef.current = true
    handleFinalPageSubmit(initialFormValues?.finalPageForm)
    // Une seule soumission auto au montage (Conforama/BUT) — pas à chaque re-render post-mutation
  }, [])

  const title = isConforamaOrBut ? loc(`${tenant}_pre_accepted_title`) : loc("Request pre-accepted")
  const paragraphOne = isConforamaOrBut
    ? loc(`${tenant}_pre_accepted_paragraph_one`)
    : loc("Pre accepted paragraph one")
  const paragraphTwo = isConforamaOrBut
    ? loc(`${tenant}_pre_accepted_paragraph_two`)
    : loc("Pre accepted paragraph two")

  const {
    data: patchOrderResult,
    mutate,
    isLoading: isMutating
  } = useMutation(patchOrder, {
    mutationKey: "patch-order",
    retry: false,
    onSuccess: (data) => {
      const v = data?.values
      if (v?.imeiNumber != null) {
        tunnelData.data.order.imeiNumber = v.imeiNumber
      }
      if (v?.bikeNumber != null) {
        tunnelData.data.order.bikeNumber = v.bikeNumber
      }
      if (v?.externalIdPartenaire != null) {
        tunnelData.data.order.externalIdPartenaire = v.externalIdPartenaire
      }
      if (v?.supplierBillNumber != null) {
        tunnelData.data.order.supplierBillNumber = v.supplierBillNumber
      }
      if (v?.supplierOrderNumber != null) {
        tunnelData.data.order.supplierOrderNumber = v.supplierOrderNumber
      }
    },
    onError: (error) => {
      console.error(error)
    }
  })

  const [isLoading, setIsLoading] = useState(false)
  const [hasCustomerRentalVoucher, setHasCustomerRentalVoucher] = useState(null)

  const { printRentalVoucher, generateRentalVoucher, error } = useOfferRentalVoucher(
    setIsLoading,
    setHasCustomerRentalVoucher
  )

  const payload = {
    transactionId: order?.id,
    documentCategory: requestDocumentCategory
  }

  const getRentalVoucher = () => {
    generateRentalVoucher(tunnelData, { transactionId: order?.id })
      .then(() => {
        printRentalVoucher(apiUrl, access_token, payload)
      })
      .catch((error) => {
        console.error("generateRentalVoucher error : \n", error)
      })
  }

  function handleFinalPageSubmit(values) {
    const v = values?.finalPageForm || values || {}
    const dto = {}
    if (finalPageNorm.numero_marquage && v.numero_marquage != null && String(v.numero_marquage).trim() !== "") {
      dto.bikeNumber = v.numero_marquage
    }
    if (finalPageNorm.imei && v.imei != null && String(v.imei).trim() !== "") {
      dto.imeiNumber = v.imei
    }
    if (finalPageNorm.id_distributeur && v.id_distributeur != null && String(v.id_distributeur).trim() !== "") {
      dto.externalIdPartenaire = v.id_distributeur
    }
    if (finalPageNorm.id_commande_distributeur && v.supplierOrderNumber != null) {
      dto.supplierOrderNumber = v.supplierOrderNumber
    }
    if (isConforamaRetailSupplierBillNumber && v.supplierBillNumber != null && String(v.supplierBillNumber).trim() !== "") {
      dto.supplierBillNumber = v.supplierBillNumber
    }
    mutate({ tunnelData, values: dto })
  }

  const validateFields = async (values) => {
    const errors = await validateFinalPageFormValues(values, {
      norm: finalPageNorm,
      tenant,
      order,
      opl: tunnelData.opl
    })
    return errors && Object.keys(errors).length > 0 ? errors : undefined
  }

  function handleReturnToHome(pdiRetailBaseUrl, tenant) {
    localStorage.clear()
    const subscriptionChannel = tunnelData?.data?.order?.subscriptionChannel
    const shopUrl = tunnelData?.newOrderShopUrl || tunnelData?.shopUrl

    window.location.href =
      subscriptionChannel === "MAGASIN" || tenant === TENANTS.CONFORAMA || tenant === TENANTS.CONFORAMA_RETAIL
        ? `${pdiRetailBaseUrl}simulation?tenant=${tenant}`
        : shopUrl || `${pdiRetailBaseUrl}login?tenant=${tenant}`
  }

  return (
    <Col xs={11} sm={10} md={10} lg={8} className="text-center hard-shadow-white-block misc-mono-block">
      <IconCheckedSuccess />
      <h1>{title}</h1>
      {tenant === TENANTS.RETAIL_IT ? (
        <div className={tenant}>
          <div>{loc(`Pre accepted paragraph one`)}</div>
          <br></br>
          <div>{loc(`Pre accepted paragraph two`)}</div>
          <div>{loc(`Pre accepted paragraph three`)}</div>
          <div>{loc(`Pre accepted paragraph four`)}</div>
          <div>{loc(`Pre accepted paragraph five`)}</div>
        </div>
      ) : (
        <div>
          <p>{paragraphOne}</p>
          <p>{paragraphTwo}</p>
          {tenant !== TENANTS.CONFORAMA && tenant !== TENANTS.CONFORAMA_RETAIL && tenant !== TENANTS.BUT && (
            <p>{loc(`Pre accepted paragraph three`)}</p>
          )}
        </div>
      )}

      <FinalForm
        decorators={[focusOnErrors]}
        validate={validateFields}
        onSubmit={handleFinalPageSubmit}
        initialValues={initialFormValues}
        render={({ handleSubmit, hasValidationErrors }) => {
          if (isMutating) {
            return <Spinner animation="border" as="div" className="spinner-block" />
          }
          return (
            <>
              <Row className="mt-4 pdi-retail-final-form">
                <Col sm={12}>
                  {isMediaMarkt && (
                    <div className="pdi-retail-final-field">
                      <TextField name="finalPageForm.opl" label={loc("pdi_retail_opl")} disabled={true} />
                    </div>
                  )}
                  {finalPageNorm.id_distributeur && (
                    <div className="pdi-retail-final-field">
                      <TextField
                        name="finalPageForm.id_distributeur"
                        label={loc("pdi_retail_store_id_label")}
                        disabled={true}
                      />
                    </div>
                  )}
                  {finalPageNorm.id_commande_distributeur && (
                    <div className="pdi-retail-final-field">
                      <TextField
                        name="finalPageForm.supplierOrderNumber"
                        label={loc("pdi_retail_partner_order_id_label")}
                        disabled={partnerOrderReadOnly}
                        inputmode={isMediaMarkt && !partnerOrderReadOnly ? "numeric" : undefined}
                      />
                    </div>
                  )}
                  {isConforamaRetailSupplierBillNumber && (
                    <div className="pdi-retail-final-field">
                      <TextField name="finalPageForm.supplierBillNumber" label={loc("bill_number")} disabled={false} />
                    </div>
                  )}
                  {finalPageNorm.numero_marquage && (
                    <div className="pdi-retail-final-field">
                      <TextField name="finalPageForm.numero_marquage" label={loc("numero_marquage")} disabled={false} />
                    </div>
                  )}
                  {finalPageNorm.imei && (
                    <div className="pdi-retail-final-field">
                      <TextField name="finalPageForm.imei" label={loc("imei")} disabled={false} />
                    </div>
                  )}
                </Col>

                <Col sm={12} className="d-flex justify-content-center mt-3">
                  {(!isConforamaOrBut || isConforamaRetailSupplierBillNumber) && (
                    <Button
                      disabled={hasValidationErrors}
                      variant={hasValidationErrors ? "secondary" : "dark"}
                      onClick={handleSubmit}
                      className="mt-0"
                    >
                      {loc(isConforamaRetailSupplierBillNumber ? "save" : "generate_delivery_voucher")}
                    </Button>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={!isMediaMarkt ? 6 : 12}>
                  <Button
                    disabled={
                      isConforamaRetailSupplierBillNumber
                        ? hasValidationErrors || (!patchOrderResult && !order?.supplierBillNumber)
                        : isConforamaOrBut
                        ? false
                        : hasValidationErrors || !patchOrderResult
                    }
                    onClick={getRentalVoucher}
                    variant="success"
                    className="ico-print-small-before button-center button-rental-voucher"
                  >
                    {loc(`Print the rental voucher`)}
                  </Button>
                </Col>
                <Col md={6}>{!isMediaMarkt && <ImportFileUploader />}</Col>
              </Row>

              {tenant !== TENANTS.RETAIL_IT && isLoading && (
                <Spinner animation="border" as="div" className="spinner-block" />
              )}

              {hasCustomerRentalVoucher === false && error === "NO_VOUCHER_FOR_ORDER" && (
                <h2 className="error-text">{loc("No rental voucher")}</h2>
              )}
              {hasCustomerRentalVoucher === false && error !== "NO_VOUCHER_FOR_ORDER" && (
                <h2 className="error-text">{loc("error_voucher_retrieval")}</h2>
              )}
              {isConforamaRetailSupplierBillNumber && patchOrderResult && (
                <h2 className="text-success">{loc("retail_patch_order_success_label")}</h2>
              )}
              {tenant !== TENANTS.RETAIL_IT && hasCustomerRentalVoucher === true && loc("voucher_ready") && (
                <h2 className="text-success">{loc("voucher_ready")}</h2>
              )}

              {tenant !== TENANTS.BUT && (
                <Row>
                  <Col>
                    <Button
                      onClick={() => handleReturnToHome(tunnelData.pdiRetailUrl, tunnelData.tenant)}
                      variant="outline-secondary"
                      className="ico-plus-before button-center"
                    >
                      {loc("New Order")}
                    </Button>
                  </Col>
                </Row>
              )}

              {finalPageNorm.finaliser_plus_tard && (
                <Row>
                  <Col className="d-flex justify-content-end">
                    <Button
                      className="w-auto"
                      variant="link"
                      onClick={() => handleReturnToHome(tunnelData.pdiRetailUrl, tunnelData.tenant)}
                    >
                      {loc("finalize_later")}
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )
        }}
      />
    </Col>
  )
}
